/**
 * Hook for managing scene selection in the V2 3-column editor.
 * Tracks which scene is currently selected and provides navigation.
 */
import { useState, useCallback, useMemo } from 'react';
import type { IReelScene } from '@/@types/reelScenes';
import type { IUseSelectedSceneReturn } from '@/@types/sceneEditorV2';

export function useSelectedScene(
  scenes: IReelScene[]
): IUseSelectedSceneReturn {
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Clamp index to valid range when scenes change
  const clampedIndex = useMemo(() => {
    if (scenes.length === 0) return 0;
    return Math.min(selectedIndex, scenes.length - 1);
  }, [selectedIndex, scenes.length]);

  const selectedScene = useMemo(() => {
    if (scenes.length === 0) return null;
    return scenes[clampedIndex] ?? null;
  }, [scenes, clampedIndex]);

  const selectScene = useCallback(
    (index: number) => {
      if (index >= 0 && index < scenes.length) {
        setSelectedIndex(index);
      }
    },
    [scenes.length]
  );

  const selectNext = useCallback(() => {
    setSelectedIndex((prev) =>
      prev < scenes.length - 1 ? prev + 1 : prev
    );
  }, [scenes.length]);

  const selectPrevious = useCallback(() => {
    setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
  }, []);

  return {
    selectedIndex: clampedIndex,
    selectedScene,
    selectScene,
    selectNext,
    selectPrevious,
  };
}
